import { saveAs } from 'file-saver';

/**
 * Converte um valor numérico em escrita por extenso em português
 * @param {number} valor - Valor a ser convertido
 * @returns {string} Valor por extenso
 */
export const valorPorExtenso = (valor) => {
  if (valor <= 0) return 'zero';
  
  const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
  const dezenas = ['', 'dez', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const dezenasEspeciais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
  
  const converterGrupo = (n, descricaoSingular, descricaoPlural) => {
    let resultado = '';
    if (n > 0) {
      if (n === 1) {
        resultado = `um ${descricaoSingular}`;
      } else {
        // Converter o grupo
        const centena = Math.floor(n / 100);
        const resto = n % 100;
        const dezena = Math.floor(resto / 10);
        const unidade = resto % 10;
        
        if (centena > 0) {
          resultado += centenas[centena];
          if (resto > 0) resultado += ' e ';
        }
        
        if (dezena === 1) {
          resultado += dezenasEspeciais[unidade];
        } else {
          if (dezena > 0) {
            resultado += dezenas[dezena];
            if (unidade > 0) resultado += ' e ';
          }
          if (unidade > 0) {
            resultado += unidades[unidade];
          }
        }
        
        resultado += ` ${descricaoPlural}`;
      }
    }
    return resultado;
  };
  
  // Separar em grupos de 3 dígitos
  const numeroStr = Math.floor(valor).toString().padStart(12, '0');
  const bilhoes = parseInt(numeroStr.substring(0, 3));
  const milhoes = parseInt(numeroStr.substring(3, 6));
  const milhares = parseInt(numeroStr.substring(6, 9));
  const unidadesGrupo = parseInt(numeroStr.substring(9, 12));
  
  let resultado = '';
  
  if (bilhoes > 0) {
    resultado += converterGrupo(bilhoes, 'bilhão', 'bilhões');
    if (milhoes > 0 || milhares > 0 || unidadesGrupo > 0) resultado += ', ';
  }
  
  if (milhoes > 0) {
    resultado += converterGrupo(milhoes, 'milhão', 'milhões');
    if (milhares > 0 || unidadesGrupo > 0) resultado += ', ';
  }
  
  if (milhares > 0) {
    resultado += converterGrupo(milhares, 'mil', 'mil');
    if (unidadesGrupo > 0) resultado += ' e ';
  }
  
  if (unidadesGrupo > 0 || resultado === '') {
    if (unidadesGrupo === 1 && resultado !== '') {
      resultado += 'um real';
    } else {
      const centena = Math.floor(unidadesGrupo / 100);
      const resto = unidadesGrupo % 100;
      const dezena = Math.floor(resto / 10);
      const unidade = resto % 10;
      
      if (centena > 0) {
        resultado += centenas[centena];
        if (resto > 0) resultado += ' e ';
      }
      
      if (dezena === 1) {
        resultado += dezenasEspeciais[unidade];
      } else {
        if (dezena > 0) {
          resultado += dezenas[dezena];
          if (unidade > 0) resultado += ' e ';
        }
        if (unidade > 0) {
          resultado += unidades[unidade];
        }
      }
      
      if (unidadesGrupo === 1) {
        resultado += ' real';
      } else {
        resultado += ' reais';
      }
    }
  }
  
  // Adicionar os centavos
  const cents = Math.round((valor - Math.floor(valor)) * 100);
  if (cents > 0) {
    resultado += ' e ';
    if (cents === 1) {
      resultado += 'um centavo';
    } else {
      const dezena = Math.floor(cents / 10);
      const unidade = cents % 10;
      
      if (dezena === 1) {
        resultado += dezenasEspeciais[unidade];
      } else {
        if (dezena > 0) {
          resultado += dezenas[dezena];
          if (unidade > 0) resultado += ' e ';
        }
        if (unidade > 0) {
          resultado += unidades[unidade];
        }
      }
      
      resultado += ' centavos';
    }
  }
  
  return resultado;
};

/**
 * Formata um valor numérico para formato de moeda brasileira
 * @param {number} valor - Valor a ser formatado
 * @returns {string} Valor formatado como moeda
 */
export const formatarValor = (valor) => {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

/**
 * Calcula o valor total do projeto somando material e mão de obra
 * @param {Array} itens - Lista de itens do projeto
 * @returns {number} Valor total
 */
export const calcularTotalGeral = (itens) => {
  let totalMaterial = 0;
  let totalMaoDeObra = 0;
  
  itens.forEach(item => {
    // Verificar se o item é do tipo "item" e não "secao"
    if (item.tipo !== 'secao') {
      const materialTotal = item.materialTotal || (item.quantidade * item.materialUnitario) || 0;
      const moTotal = item.moTotal || (item.quantidade * item.moUnitario) || 0;
      
      totalMaterial += parseFloat(materialTotal);
      totalMaoDeObra += parseFloat(moTotal);
    }
  });
  
  return totalMaterial + totalMaoDeObra;
};

/**
 * Gera um documento Word para o orçamento ativo
 * @param {Object} formData - Dados do formulário
 * @param {Object} activeProject - Projeto ativo
 * @param {Array} projectItems - Itens do projeto
 */
export const gerarDocumentoWord = async (formData, activeProject, projectItems) => {
  if (!activeProject) {
    console.error('Nenhum orçamento selecionado');
    return;
  }
  
  try {
    const valorTotal = calcularTotalGeral(projectItems);
    const dataAtual = new Date().toLocaleDateString('pt-BR');
    
    // Modelo de documento Word
    const MODELO_DOCUMENTO = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" 
      xmlns:w="urn:schemas-microsoft-com:office:word" 
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Proposta Comercial KMF</title>
<style>
    @page {
        size: A4;
        margin: 2cm;
    }
    
    @media screen and (max-width: 768px) {
    body {
            margin: 20px;
        }
        .title {
            font-size: 16pt;
        }
        .index {
            position: relative;
            top: auto;
            right: auto;
            text-align: center;
            margin-bottom: 15px;
        }
        .date {
            text-align: center;
        }
        .section {
            padding: 10px;
        }
    }
    
    body { 
        font-family: Calibri, sans-serif; 
        line-height: 1.6;
        margin: 40px;
        color: #333;
        background-color: #fff;
    }
    
    .header {
        text-align: left; 
        margin-bottom: 40px;
        position: relative;
        border-bottom: 1px solid #fe8138;
        padding-bottom: 15px;
    }
    
    .title {
        font-size: 18pt; 
        font-weight: bold;
        margin-bottom: 20px;
        color: #fe8138;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .index {
        position: absolute;
        top: 0;
        right: 0;
        font-weight: bold;
        color: #fe8138;
        font-size: 14pt;
        background-color: #f8f9fa;
        padding: 5px 10px;
        border-radius: 5px;
        white-space: nowrap;
    }
    
    .date {
        text-align: right;
        font-weight: normal;
        margin-bottom: 40px;
        font-style: italic;
        white-space: nowrap;
    }
    
    .spacing { 
        margin: 30px 0;
        height: 15px;
    }
    
    .section { 
        margin: 25px 0;
        background-color: #f8f9fa;
        padding: 15px;
        border-radius: 5px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }
    
    .section-title { 
        font-weight: bold;
        font-size: 10pt;
        margin-bottom: 15px;
        color: #fe8138;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 8px;
        white-space: nowrap;
    }
    
    .section p {
        text-align: justify;
        margin: 10px 0;
    }
    
    .destaque {
        font-weight: bold;
        color: #fe8138;
        white-space: nowrap;
    }
    
    .footer {
        margin-top: 40px;
        page-break-inside: avoid;
        border-top: 1px solid #dee2e6;
        padding-top: 20px;
    }
    
    .signature {
        margin-top: 60px;
        page-break-inside: avoid;
        text-align: center;
        padding: 20px;
        background-color: #f8f9fa;
        border-radius: 5px;
    }
    
    ul {
        list-style-type: square;
        margin-left: 20px;
    }
    
    li {
        margin-bottom: 8px;
    }
    
    @media print {
        body {
            margin: 0;
            padding: 0;
        }
        
        .section {
            page-break-inside: avoid;
        }
        
        .section-title {
            page-break-after: avoid;
        }
        
        .signature {
            page-break-inside: avoid;
        }
    }
</style>
</head>
<body>
    <div class="header">
        <p class="title">Proposta Comercial - [CLIENTE] - [TIPO_SERVICO]</p>
        <p class="date">[CIDADE], [DATA_ATUAL]</p>
    </div>
    
    <div class="spacing"></div>

    <p>A <span class="destaque">[CLIENTE]</span></p>

    <div class="spacing"></div>
    <div class="spacing"></div>

    <p>A/C</p>

    <div class="spacing"></div>

    <p>Ref. Proposta para <span class="destaque">[TIPO_SERVICO]</span></p>

    <div class="section">
        <p class="section-title">1. OBJETO</p>
        <p>O objeto da presente proposta é apresentar ao <span class="destaque">[CLIENTE]</span>, as condições pelas quais a KMF ENGENHARIA LTDA., situada na Rua DONA MARIA MAXIMO, 153 – Ponta da Praia – 11030-101 - Santos – SP, se propõe a executar os serviços contidos na carta convite e seus anexos.</p>
    </div>
    
    <div class="section">
        <p class="section-title">2. ESCOPO DA KMF</p>
        <p>Execução dos serviços descritos na solicitação de proposta orçados na Proposta Comercial - <span class="destaque">[TIPO_SERVICO]</span>, seguindo todas as exigências técnicas e executivas solicitadas.</p>
    </div>

    <div class="section">
        <p class="section-title">3. ESCOPO DO [CLIENTE]</p>
        <p>Acesso nas áreas necessárias para execução da obra. Sendo que a identificação com crachá é de responsabilidade da KMF ENGENHARIA LTDA.</p>
        <p>Fornecimento de ponto de água e energia elétrica próximos ao canteiro e as frentes de obra, material para realização dos serviços.</p>
    </div>

    <div class="section">
        <p class="section-title">4. DESENHOS E DOCUMENTOS DE REFERÊNCIA</p>
        <p>Para a execução dessa proposta analisou-se e considerou-se os seguintes documentos:</p>
        <ul>
            <li>Visita técnica</li>
        </ul>
        <p>Esta proposta não considera a incidência de interferências significativas. Caso algum evento de magnitude significativa (da ordem de 10% do valor de contrato) se faça necessário, os valores desta proposta deverão ser revisados.</p>
    </div>
    
    <div class="section">
        <p class="section-title">5. DOCUMENTOS ANEXOS E COMPONENTES DESSA PROPOSTA</p>
        <p>São partes componentes dessa proposta os documentos:</p>
        <ul>
            <li>A planilha orçamentária - <span class="destaque">[NOME_PROJETO]</span></li>
        </ul>
    </div>
    
    <div class="section">
        <p class="section-title">6. REMUNERAÇÃO, CONDIÇÕES, IMPOSTOS E FATURAMENTO DIRETO</p>
        <p>O valor total de contrato para realização dos serviços acima descritos será de <span class="destaque">[VALOR_TOTAL]</span> (<span class="destaque">[VALOR_POR_EXTENSO]</span>), referente para fornecimento de mão de obra e equipamentos.</p>
        <p>A data base considerada para esses valores são <span class="destaque">[DATA_BASE]</span>. O reajuste será feito em 1 meses de acordo com o índice do INCC, vigente no respectivo período, apenas se maior que o valor acordado.</p>
        <p>Esta proposta considera o pagamento dos impostos INSS (11% da mão de obra) e ISS (3%) retidos em nota e o pagamento de PIS (0,65%), COFINS (3,00%), IRPJ (estimado 1,5%) e CSL (1,08%).</p>
        <p>Será considerado fornecimento de EPI, Equipamentos e Almoço pela construtora.</p>
    </div>

    <div class="section">
        <p class="section-title">7. PRAZO DE EXECUÇÃO</p>
        <ul>
            <li>A proposta prevê um total de 30 dias.</li>
            <li>A mobilização será feita imediatamente após a emissão da carta de contratação.</li>
            <li>O início da obra acontecerá de imediato a emissão da carta de contratação, aceite da proposta e liberação de campo área.</li>
        </ul>
    </div>

    <div class="section">
        <p class="section-title">8. FORMA DE PAGAMENTO, PRAZO E CONDIÇÕES</p>
        <p>Pagamentos, serão feitos contra medição mensal, conforme avanço físico. Está considerado o pagamento no período de 30 dias após a emissão da Nota Fiscal.</p>
        <p>Ao que tange os valores das medições da KMF Engenharia:</p>
        <ul>
            <li>95% do valor contrato será pago através de medições, conforme avanço físico do cronograma detalhado e aprovado entre as partes e 5% após conclusão dos serviços e aceite final do recebimento e entrega de relatório final dos serviços.</li>
            <li>Sinal de [SINAL] do valor do contrato, valor será retido nas medições.</li>
        </ul>
        <p>Será permitida a contratação de terceiros, mediante aprovação da <span class="destaque">[CLIENTE]</span>.</p>
        <p>A validade da proposta é de 30 (trinta) dias.</p>
    </div>

    <div class="section">
        <p class="section-title">9. GARANTIA E SEGURO</p>
        <p>Está prevista garantia conforme o Código Civil Brasileiro. Em relação aos produtos utilizados, conforme especificações dos fabricantes.</p>
        <p>Estão considerados o Seguro de Vida dos funcionários que trabalharem no local, seguro de Risco de Engenharia do valor contratado, seguro dos veículos e equipamentos considerados para a obra.</p>
    </div>

    <div class="section">
        <p class="section-title">10. COMENTÁRIOS</p>
        <p>Esta proposta garante o atendimento de todas as diretrizes expostas nos documentos enviados, atividade de segunda a sexta-feira.</p>
        <p>Todos os materiais serão de fornecimento da KMF Engenharia.</p>
        <p>O valor apresentado reflete a execução do serviço por meio de escavação manual. Caso seja autorizada a utilização de retroescavadeira, conseguiremos uma melhor condição comercial.</p>
    </div>

    <div class="footer">
        <p>Agradecemos a oportunidade do convite e colocamo-nos ao inteiro dispor de V.Sas. para quaisquer esclarecimentos que se façam necessários.</p>
        <p>Sem mais para o momento, subscrevemo-nos.</p>
        <p>Atenciosamente,</p>
    </div>

    <div class="signature">
        <p><strong>KMF ENGENHARIA LTDA</strong></p>
        <p>CREA: 2563795</p>
        <p><strong>Engº Mário Henrique Pugliese Balduino</strong></p>
        <p>CREA: 507.070.088-8</p>
    </div>
</body>
</html>`;
    
    // Substituir os placeholders pelos valores reais
    let conteudo = MODELO_DOCUMENTO
      .replace(/\[CLIENTE\]/g, formData.nomeSolicitante)
      .replace(/\[TIPO_SERVICO\]/g, formData.tipoServico)
      .replace(/\[CIDADE\]/g, 'São Paulo')
      .replace(/\[DATA_ATUAL\]/g, dataAtual)
      .replace(/\[NOME_PROJETO\]/g, activeProject.nome || activeProject.name)
      .replace(/\[VALOR_TOTAL\]/g, formatarValor(valorTotal))
      .replace(/\[VALOR_POR_EXTENSO\]/g, valorPorExtenso(valorTotal))
      .replace(/\[DATA_BASE\]/g, formData.dataBase)
      .replace(/\[SINAL\]/g, `${formData.percentualEntrada}%`);
    
    // Criar um Blob com o conteúdo formatado para HTML (o Word consegue abrir)
    const blob = new Blob([conteudo], { type: 'application/msword' });
    
    // Criar URL para download
    const url = URL.createObjectURL(blob);
    
    // Criar link de download
    const a = document.createElement('a');
    a.href = url;
    a.download = `Proposta_${activeProject.nome ? activeProject.nome.replace(/\s+/g, '_') : activeProject.name.replace(/\s+/g, '_')}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    return true;
  } catch (error) {
    console.error('Erro ao gerar documento Word:', error);
    return false;
  }
}; 